import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { Markdown } from '@tiptap/markdown';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

type MarkdownExtensionFactoryOptions = {
  placeholder?: string;
};

const DEFAULT_PLACEHOLDER = 'Add notes... (Markdown supported)';

/**
 * TipTap extensions configured for markdown editing in task notes.
 * This factory returns fresh extension instances so multiple editors don't share state.
 */
export function createMarkdownExtensions(options: MarkdownExtensionFactoryOptions = {}) {
  const placeholderText = options.placeholder ?? DEFAULT_PLACEHOLDER;

  return [
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
        keepAttributes: true,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: true,
      },
      codeBlock: {
        HTMLAttributes: {
          class: 'rounded-lg border border-border/40 bg-muted/40 p-3 font-mono text-xs leading-relaxed',
        },
      },
      heading: {
        levels: [1, 2, 3, 4, 5, 6],
      },
      // Disable the default link extension since we're adding our own
      link: false,
    }),
    Markdown,
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
      HTMLAttributes: {
        class: 'text-primary underline decoration-primary/40 underline-offset-2',
      },
    }),
    Underline,
    Highlight.configure({
      multicolor: true,
      HTMLAttributes: {
        class: 'bg-warning-surface/60 text-foreground rounded-sm px-0.5',
      },
    }),
    TaskList.configure({
      HTMLAttributes: {
        class: 'not-prose space-y-1',
      },
    }),
    TaskItem.configure({
      nested: true,
      HTMLAttributes: {
        class: 'flex items-start gap-2',
      },
    }),
    Table.configure({
      resizable: false,
      HTMLAttributes: {
        class: 'border-collapse border border-border/40 rounded-md overflow-hidden',
      },
    }),
    TableRow,
    TableHeader.configure({
      HTMLAttributes: {
        class: 'border border-border/40 bg-muted/30 px-3 py-2 font-semibold',
      },
    }),
    TableCell.configure({
      HTMLAttributes: {
        class: 'border border-border/40 px-3 py-2',
      },
    }),
    Placeholder.configure({
      placeholder: placeholderText,
    }),
  ];
}
